import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { openHelpPanel } from '@/help/helpPanelStore';
import { surfaceForPath } from '@/help/surfaceRegistry';
import { useTour } from '@/edu/TourContext';
import { CommandPalette, type PaletteSource } from './CommandPalette';
import { surfaceTargets } from './navTargets';
import { loadEntityItems, loadFlowItems } from './paletteSources';
import type { PaletteItem } from './paletteModel';
import { closePalette, recordRecent, togglePalette, usePaletteOpen, useRecents } from './paletteStore';

/**
 * Global command palette host (OPSFL-POLISH-1). Mounted ONCE in the app shell — like HelpPanelHost —
 * so every surface gets the palette with zero per-page edits (including the pages owned by other
 * lanes).
 *
 * Hotkey: Cmd/Ctrl+K. Deliberately NOT suppressed inside text fields: it is a modifier chord that
 * no input consumes, and an operator half-way through typing a filter is exactly who needs to jump
 * elsewhere. Unmodified single-key shortcuts (`?`, `j`/`k`, disposition digits) ARE suppressed
 * while typing — that rule is what protects the text fields.
 */
export function CommandPaletteHost() {
  const open = usePaletteOpen();
  const navigate = useNavigate();
  const location = useLocation();
  const { loadTours, startTour, relaunchFirstRun } = useTour();
  const recents = useRecents();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'k' && e.key !== 'K') return;
      if (!(e.metaKey || e.ctrlKey) || e.altKey || e.repeat) return;
      e.preventDefault();
      togglePalette();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const surfaces = useMemo(() => surfaceTargets(), []);

  const startBestTour = useCallback(async () => {
    const tours = await loadTours(surfaceForPath(location.pathname)).catch(() => []);
    if (tours.length > 0) startTour(tours[0]);
    else await relaunchFirstRun();
  }, [loadTours, startTour, relaunchFirstRun, location.pathname]);

  const actions = useMemo<PaletteItem[]>(
    () => [
      {
        id: 'action:new-flow',
        kind: 'action',
        label: 'New flow',
        hint: 'Open the Workflow Designer on a blank draft',
        keywords: 'create workflow designer draft',
        route: '/flows/new',
      },
      {
        id: 'action:new-screen-config',
        kind: 'action',
        label: 'New screen config',
        hint: 'Start a dataroom journey in Screen Studio',
        keywords: 'create screen studio journey dataroom',
        route: '/screens?new=1',
      },
      {
        id: 'action:help',
        kind: 'action',
        label: 'Open help for this page',
        hint: 'The same guide as the ? key',
        keywords: 'about this page documentation guide',
        run: openHelpPanel,
      },
      {
        id: 'action:tour',
        kind: 'action',
        label: 'Start tour',
        hint: 'Guided walkthrough of this surface',
        keywords: 'guided walkthrough onboarding first run',
        run: () => void startBestTour(),
      },
    ],
    [startBestTour],
  );

  // Recents re-resolve against the LIVE rows so an imperative action still carries its behaviour;
  // a flow/entity recent stays usable from its stored route with no fetch.
  const recentItems = useMemo<PaletteItem[]>(() => {
    const byId = new Map<string, PaletteItem>([...surfaces, ...actions].map((i) => [i.id, i]));
    return recents.map((r) => byId.get(r.id) ?? { ...r });
  }, [recents, surfaces, actions]);

  // Flows are only offered once something is typed: eight arbitrary flows on open would be noise,
  // not navigation.
  const searchFlows = useCallback<PaletteSource>(async (q) => (q.trim() ? loadFlowItems() : []), []);
  const searchEntities = useCallback<PaletteSource>((q) => loadEntityItems(q), []);

  const onSelect = useCallback(
    (item: PaletteItem) => {
      recordRecent({
        id: item.id,
        kind: item.kind,
        label: item.label,
        hint: item.hint,
        route: item.route,
        meta: item.meta,
      });
      closePalette();
      if (item.run) item.run();
      else if (item.route) navigate(item.route);
    },
    [navigate],
  );

  return (
    <CommandPalette
      open={open}
      onClose={closePalette}
      surfaces={surfaces}
      actions={actions}
      recents={recentItems}
      searchFlows={searchFlows}
      searchEntities={searchEntities}
      onSelect={onSelect}
    />
  );
}
